<script>
/**
 * Inline canvas-based signature pad. Emits 'change' with a base64 PNG (no data URI prefix).
 * Usage:
 *   <SignaturePad ref="pad" :height="160" @change="onSigChange" />
 *   this.$refs.pad.clear() | this.$refs.pad.toDataURL() | this.$refs.pad.isEmpty()
 */
export default {
  name: "SignaturePad",
  props: {
    height: { type: Number, default: 160 },
    backgroundColor: { type: String, default: "#fff" },
    penColor: { type: String, default: "#222" },
  },
  data() {
    return { drawing: false, last: null, dirty: false };
  },
  mounted() {
    this.resize();
    window.addEventListener("resize", this.resize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resize);
  },
  methods: {
    resize() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const data = this.dirty ? this.toDataURL() : null;
      canvas.width = canvas.offsetWidth;
      canvas.height = this.height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = this.backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (data) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        img.src = data;
      }
    },
    pos(e) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    },
    onStart(e) {
      e.preventDefault();
      this.drawing = true;
      this.last = this.pos(e);
    },
    onMove(e) {
      if (!this.drawing) return;
      e.preventDefault();
      const ctx = this.$refs.canvas.getContext("2d");
      const cur = this.pos(e);
      ctx.strokeStyle = this.penColor;
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(this.last.x, this.last.y);
      ctx.lineTo(cur.x, cur.y);
      ctx.stroke();
      this.last = cur;
      this.dirty = true;
    },
    onEnd() {
      if (this.drawing) {
        this.drawing = false;
        this.$emit("change", this.toDataURL());
      }
    },
    clear() {
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = this.backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      this.dirty = false;
      this.$emit("change", "");
    },
    isEmpty() { return !this.dirty; },
    toDataURL() {
      const url = this.$refs.canvas.toDataURL("image/png");
      // Strip "data:image/png;base64," prefix to match Flutter format.
      return url.replace(/^data:image\/png;base64,/, "");
    },
  },
};
</script>

<template>
  <div class="signature-pad">
    <canvas
      ref="canvas"
      @mousedown="onStart"
      @mousemove="onMove"
      @mouseup="onEnd"
      @mouseleave="onEnd"
      @touchstart="onStart"
      @touchmove="onMove"
      @touchend="onEnd"
    ></canvas>
    <div class="actions text-right">
      <button type="button" class="btn btn-link btn-sm text-danger" @click="clear">
        <i class="mdi mdi-eraser mr-1"></i>Clear
      </button>
    </div>
  </div>
</template>

<style scoped>
.signature-pad canvas {
  width: 100%;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
  touch-action: none;
  cursor: crosshair;
}
.signature-pad .actions { margin-top: 4px; }
</style>
